import type { FC, ReactNode } from "react";

import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

type RowProps = {
  title: ReactNode;
  desc?: ReactNode;
  children?: ReactNode;
};

export const SettingRow: FC<RowProps> = ({ title, desc, children }) => (
  <div className="flex items-start justify-between gap-6 border-b border-line-2 py-3.5 last:border-b-0">
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-sm font-medium text-fg">{title}</span>
      {desc ? (
        <span className="text-sm leading-4.5 text-fg-3">{desc}</span>
      ) : null}
    </div>
    {children ? (
      <div className="flex shrink-0 items-center justify-end gap-2">
        {children}
      </div>
    ) : null}
  </div>
);

export const SettingGroup: FC<{ label: string; children: ReactNode }> = ({
  label,
  children
}) => (
  <div className="mt-5 flex flex-col first:mt-0">
    <span className="text-sm font-medium text-fg-3">{label}</span>
    {children}
  </div>
);

type SegmentedProps<T extends string> = {
  label: string;
  value: T;
  options: ReadonlyArray<{ id: T; label: string }>;
  onChange: (value: T) => void;
  disabled?: boolean;
};

export const Segmented = <T extends string>({
  label,
  value,
  options,
  onChange,
  disabled
}: SegmentedProps<T>) => (
  <ToggleGroup
    type="single"
    variant="outline"
    size="sm"
    spacing={0}
    aria-label={label}
    value={value}
    disabled={disabled}
    onValueChange={(next: string) => {
      const match = options.find((o) => o.id === next);
      if (match) onChange(match.id);
    }}
  >
    {options.map((o) => (
      <ToggleGroupItem
        key={o.id}
        value={o.id}
        className="px-2.5 text-sm font-normal text-fg-3 data-[state=on]:bg-primary/10 data-[state=on]:font-medium data-[state=on]:text-primary"
      >
        {o.label}
      </ToggleGroupItem>
    ))}
  </ToggleGroup>
);
